import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi, uploadImage } from "../../lib/api";
import { mediaUrl } from "../../lib/format";
import { toast } from "sonner";
import BlockEditor from "../../components/BlockEditor";
import { Save, ArrowLeft, X, Plus } from "lucide-react";

const EMPTY = {
  name: "", reference: "", price: 0, weight_kg: 0, min_qty: 1, short_description: "", description: "",
  extended_description: "", images: [], features: {}, category_ids: [],
  featured: false, published: true, seo_title: "", meta_description: "", keywords: "", faq: [], slug: "",
  featured_start: "", featured_end: "",
  banner: "", content_blocks: [],
};

export default function AdminProductEdit() {
  const { id } = useParams();
  const isNew = id === "nuevo";
  const nav = useNavigate();
  const [p, setP] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [imgInput, setImgInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/categories").then((r) => setCats(r.data));
    if (!isNew) adminApi.get(`/admin/products/${id}`).then((r) => setP({ ...EMPTY, ...r.data }));
  }, [id, isNew]);

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...p, price: parseFloat(p.price) || 0, weight_kg: parseFloat(p.weight_kg) || 0, min_qty: Math.max(1, parseInt(p.min_qty) || 1) };
      if (isNew) await adminApi.post("/admin/products", payload);
      else await adminApi.put(`/admin/products/${id}`, payload);
      toast.success("Producto guardado");
      nav("/admin/productos");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleCat = (cid) => {
    set("category_ids", p.category_ids.includes(cid) ? p.category_ids.filter((c) => c !== cid) : [...p.category_ids, cid]);
  };

  return (
    <div className="max-w-4xl">
      <button onClick={() => nav("/admin/productos")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand mb-4"><ArrowLeft className="h-4 w-4" /> Volver</button>
      <h1 className="font-heading font-bold text-3xl tracking-tighter mb-6">{isNew ? "Nuevo producto" : "Editar producto"}</h1>

      <div className="space-y-5">
        <div className="bg-white border border-border rounded-sm p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className="text-sm font-medium block mb-1">Nombre *</label><input value={p.name} onChange={(e) => set("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="edit-name" /></div>
            <div><label className="text-sm font-medium block mb-1">Referencia</label><input value={p.reference} onChange={(e) => set("reference", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm font-mono" data-testid="edit-reference" /></div>
            <div><label className="text-sm font-medium block mb-1">Precio (€)</label><input type="number" step="0.01" value={p.price} onChange={(e) => set("price", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="edit-price" /></div>
            <div><label className="text-sm font-medium block mb-1">Peso (kg) <span className="text-xs text-muted-foreground font-normal">para calcular portes</span></label><input type="number" step="0.01" min="0" value={p.weight_kg ?? 0} onChange={(e) => set("weight_kg", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="edit-weight" /></div>
            <div><label className="text-sm font-medium block mb-1">Cantidad mínima de compra</label><input type="number" step="1" min="1" value={p.min_qty ?? 1} onChange={(e) => set("min_qty", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="edit-min-qty" /></div>
          </div>
          <div><label className="text-sm font-medium block mb-1">Descripción corta</label><textarea rows={2} value={p.short_description} onChange={(e) => set("short_description", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" /></div>
          <div><label className="text-sm font-medium block mb-1">Descripción</label><textarea rows={5} value={p.description} onChange={(e) => set("description", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" /></div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={p.published} onChange={(e) => set("published", e.target.checked)} data-testid="edit-published" /> Publicado</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={p.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="edit-featured" /> Destacado</label>
            {p.featured && (
              <div className="grid grid-cols-2 gap-3 pl-6 pt-1">
                <div><label className="text-xs text-muted-foreground block mb-1">Destacado desde (opcional)</label><input type="date" value={p.featured_start || ""} onChange={(e) => set("featured_start", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="edit-featured-start" /></div>
                <div><label className="text-xs text-muted-foreground block mb-1">Hasta (opcional)</label><input type="date" value={p.featured_end || ""} onChange={(e) => set("featured_end", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="edit-featured-end" /></div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-border rounded-sm p-5">
          <label className="text-sm font-medium block mb-2">Imágenes (URL)</label>
          <div className="flex gap-2 mb-3">
            <input value={imgInput} onChange={(e) => setImgInput(e.target.value)} placeholder="https://..." className="flex-1 px-3 py-2 text-sm border border-border rounded-sm" />
            <button onClick={() => { if (imgInput) { set("images", [...p.images, imgInput]); setImgInput(""); } }} className="bg-brand text-white px-3 rounded-sm"><Plus className="h-4 w-4" /></button>
          </div>
          <label className="inline-block text-xs text-brand cursor-pointer hover:underline mb-3" data-testid="product-upload-label">
            Subir imagen desde el ordenador
            <input type="file" accept="image/*" className="hidden" data-testid="product-image-upload" onChange={async (e) => { const f = e.target.files[0]; if (f) { try { const url = await uploadImage(f); set("images", [...p.images, url]); toast.success("Imagen subida"); } catch { toast.error("Error al subir"); } } }} />
          </label>
          <div className="flex flex-wrap gap-2">
            {p.images.map((img, i) => (
              <div key={i} className="relative w-20 h-20 border border-border rounded-sm overflow-hidden bg-secondary">
                <img src={img} alt="" className="w-full h-full object-contain" />
                <button onClick={() => set("images", p.images.filter((_, idx) => idx !== i))} className="absolute top-0 right-0 bg-destructive text-white p-0.5"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border rounded-sm p-5">
          <label className="text-sm font-medium block mb-2">Categorías</label>
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <button key={c.id} onClick={() => toggleCat(c.id)} className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${p.category_ids.includes(c.id) ? "bg-brand text-white border-brand" : "border-border hover:border-brand"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-border rounded-sm p-5">
          <label className="text-sm font-medium block mb-2">Imagen de banner (cabecera de la ficha)</label>
          <div className="flex gap-2 items-start">
            <input value={p.banner || ""} onChange={(e) => set("banner", e.target.value)} placeholder="URL o suba un archivo" className="flex-1 px-3 py-2 text-sm border border-border rounded-sm" data-testid="edit-banner" />
            {p.banner && <img src={mediaUrl(p.banner)} alt="" className="h-12 w-20 object-cover rounded-sm border border-border" />}
          </div>
          <label className="inline-block text-xs text-brand cursor-pointer hover:underline mt-1">
            Subir imagen desde el ordenador
            <input type="file" accept="image/*" className="hidden" data-testid="product-banner-upload" onChange={async (e) => { const f = e.target.files[0]; if (f) { try { const url = await uploadImage(f); set("banner", url); toast.success("Imagen subida"); } catch { toast.error("Error al subir"); } } }} />
          </label>
        </div>

        <div className="bg-white border border-border rounded-sm p-5">
          <label className="text-sm font-medium block mb-2">Contenido adicional de la ficha</label>
          <p className="text-xs text-muted-foreground mb-3">Bloques libres (texto, imágenes o HTML) que se muestran en la página del producto, bajo la descripción.</p>
          <BlockEditor value={p.content_blocks || []} onChange={(v) => set("content_blocks", v)} testidPrefix="product-block" />
        </div>

        <div className="bg-white border border-border rounded-sm p-5 space-y-4">
          <p className="font-heading font-semibold">SEO</p>
          <div><label className="text-sm font-medium block mb-1">Meta title <span className="text-xs text-muted-foreground font-normal">({(p.seo_title || "").length}/70)</span></label><input value={p.seo_title} onChange={(e) => set("seo_title", e.target.value)} maxLength={70} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="product-seo-title" /></div>
          <div><label className="text-sm font-medium block mb-1">Keywords <span className="text-xs text-muted-foreground font-normal">(separadas por comas)</span></label><input value={p.keywords || ""} onChange={(e) => set("keywords", e.target.value)} placeholder="bandeja aluminio, bandeja panadería, ..." className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="product-keywords" /></div>
          <div><label className="text-sm font-medium block mb-1">Meta description</label><textarea rows={2} value={p.meta_description} onChange={(e) => set("meta_description", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" /></div>
        </div>

        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-sm transition-colors" data-testid="save-product-btn">
          <Save className="h-4 w-4" /> {saving ? "Guardando..." : "Guardar producto"}
        </button>
      </div>
    </div>
  );
}
